import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import TextButton from 'LuxKit/Button/TextButton'
import { getNotificationType, hasAccountNotification } from 'selectors/accountSelectors'
import FormatCurrency from 'components/Common/FormatCurrency'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import Caption from 'LuxKit/Typography/Caption'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import NavigationHeaderTripsMenu from './NavigationHeaderTripsMenu'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import AgentHubAccountMenu from 'agentHub/components/AgentHubAccountMenu'
import AgentHubCustomerViewSwitch from 'agentHub/components/AgentHubCustomerViewSwitch'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import LineSuitcaseIcon from 'LuxKit/Icons/line/LineSuitcaseIcon'
import { trackClientEvent } from 'analytics/analytics'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import HeaderNotification from '../HeaderNotification'
import MultiCartHeader from '../MultiCartHeader'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { isAgentHubCVFREnabled, isAgentHubEnabled, isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/agentHubSelectors'
import formatStaticAppText from 'lib/format/formatStaticAppText'

interface Props {
  isLoggedIn: boolean
  className?: string
  hasNotification: boolean
  firstName?: string
  lastName?: string
  balance: number
  canRedeemLuxPlusBenefits: boolean
  notificationType: App.Design.ContentColour
  loyaltyAccount: App.LoyaltyAccount
  isLoyaltyEnabled: boolean
  isSpoofed: boolean
}

function NavigationHeader(props: Props) {
  const intl = useIntl()
  const {
    isLoggedIn,
    className,
    hasNotification,
    firstName,
    lastName,
    balance,
    canRedeemLuxPlusBenefits,
    notificationType,
    loyaltyAccount,
    isLoyaltyEnabled,
    isSpoofed,
  } = props

  const showModal = useContext(ModalContext)
  const showLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const onMyEscapesClick = () => {
    trackClientEvent({
      subject: 'my_escapes',
      action: 'click',
      category: 'logging',
      type: 'interaction',
    })
  }

  const accountLabel = intl.formatMessage({
    id: 'HfNqtZ-nVBaK0V8f5Bbc',
    defaultMessage: 'Account',
    description: 'Account caption text in navigation header in account settings',
  })

  const myEscapesLabel = useMemo(() => {
    const purchaseSynonym = config.brandConfig.purchaseSynonym
    if (purchaseSynonym === 'booking') {
      return intl.formatMessage({
        id: '_osIGfFeHN7UzcieCbG0',
        defaultMessage: 'My Bookings',
        description: 'Navigation label for My Bookings section in the booking flow',
      })
    }
    if (purchaseSynonym === 'escape') {
      return intl.formatMessage({
        id: 'H01E5qMD_amz1GJla5s9',
        defaultMessage: 'My Escapes',
        description: 'Navigation label for My Escapes section in the site header',
      })
    }
    return intl.formatMessage({
      id: 'ycbrgHJL2UVEnyrA0v6v',
      defaultMessage: 'My Purchases',
      description: 'Navigation label for My Purchases section in the site header',
    })
  }, [intl])

  const isMyEscapesEnabled = useMemo(() => {
    if (!isAgentHubEnabled) return true
    return isAgentHubCVFREnabled || isSpoofed
  }, [isSpoofed])

  return (
    <Group direction="horizontal" gap={8} className={className}>
      <TripGuard>
        <NavigationHeaderTripsMenu />
      </TripGuard>
      {isLoggedIn && <>
        {isAgentHubEnabled && <AgentHubCustomerViewSwitch />}
        {isMyEscapesEnabled && (
          <TextButton
            startIcon={<LineSuitcaseIcon colour="primary" />}
            kind="tertiary"
            variant="dark"
            onClick={onMyEscapesClick}
            to="/account/my-escapes"
          >
            {myEscapesLabel}
          </TextButton>
        )}
        {isAgentHubEnabled && <AgentHubAccountMenu />}
        {isLoggedIn && <MultiCartHeader />}
        {config.NOTIFICATION_CENTRE_ENABLED && <HeaderNotification />}
        {/* TODO - remove/update. This is a temp UI implementation */}
        {!isLuxLoyaltyEnabledInAgentHub && isLoyaltyEnabled && !!loyaltyAccount && <div>
          <Caption variant="medium" weight="bold">{getLoyaltyProgramDisplayName()}</Caption>
          <Caption variant="medium">
            {getLoyaltyTierDisplayName(loyaltyAccount.tier)}
          </Caption>
          {loyaltyAccount.points > 0 && <Caption variant="medium" format="titlecase">
            <FormatLoyaltyPoints points={loyaltyAccount.points} />
          </Caption>}
        </div>}
        {config.header.standAloneAccount && (
          <ProductPaletteProvider palette={canRedeemLuxPlusBenefits ? 'lux-plus' : 'default'}>
            <TextButton
              kind="tertiary"
              to="/account"
              aria-label={intl.formatMessage({
                id: 'z_R4eN5SbivCvxPGkkfw',
                defaultMessage: 'Account',
                description: 'Aria label for account button in navigation header',
              })}
              startIcon={<AvatarCircle
                size="small"
                firstName={firstName}
                lastName={lastName}
                hasNotification={hasNotification}
                nonInteractable
                notificationType={notificationType}
              />}
            >
              <Group direction="vertical" horizontalAlign="start">
                <Caption variant="medium" weight="bold">
                  {canRedeemLuxPlusBenefits ? formatStaticAppText(intl, LUXURY_PLUS.PROGRAM_NAME) : accountLabel}
                </Caption>
                {balance > 0 &&
                  <Caption variant="medium">
                    <FormatCurrency value={balance} format="dynamicDollar" />
                  </Caption>}
              </Group>
            </TextButton>
          </ProductPaletteProvider>
        )}
      </>}

      {!isLoggedIn && (
        <TextButton
          kind="primary"
          onClick={showLogin}
          role="menuitem"
          name="signup"
        >
          {intl.formatMessage({
            id: 'CSp_iI2u7P-b31PESnc6',
            defaultMessage: 'Log in',
            description: 'Log in button text in the navigation header in the site header',
          })}
        </TextButton>
      )}
    </Group>
  )
}

function mapStateToProps(state: App.State) {
  return {
    hasNotification: hasAccountNotification(state),
    balance: state.auth.account.balance,
    isFlightsCredit: isFlightsCredit(state),
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
    notificationType: getNotificationType(state),
    loyaltyAccount: state.loyalty.account.data,
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
    isSpoofed: state.auth.account.isSpoofed,
  }
}

export default connect(mapStateToProps)(NavigationHeader)
